import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  HStack,
  Link,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as GatsbyLink } from 'gatsby';
import React, { useState, useEffect } from 'react';

import { SectionHeader } from '../SectionHeader';

const SLIDE_DURATION = 5000;

const ProgressIndicator = ({ isActive }: { isActive: boolean }) => (
  <Box transform={['rotate(-90deg)', 'rotate(0)']}>
    <Box
      as="svg"
      width="12"
      height="64"
      viewBox="0 0 12 64"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="12" height="64" rx={6} fill="#D0DCE1" />
      <motion.rect
        width="12"
        height="64"
        rx={6}
        fill="#012B3B"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isActive ? 1 : 0 }}
        transition={{ duration: SLIDE_DURATION / 1000, ease: 'linear' }}
        style={{ originY: 0 }}
      />
    </Box>
  </Box>
);

interface Feature {
  icon: {
    url: string;
  };
  text: string;
}

interface SolutionSliderProps {
  backgroundColor?: string;
  componentName: string;
  title: string;
  headline: string;
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  disclaimer?: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
  container: Array<{
    slides: Array<{
      slideHeading: string;
      description: {
        childMarkdownRemark: {
          rawMarkdownBody: string;
        };
      };
      navigationLabel: string;
      backgroundImage: {
        file: {
          url: string;
        };
      };
      mobileBackgroundImage?: {
        file: {
          url: string;
        };
      };
      ctaButton?: {
        url: string;
        label: string;
      };
      features?: Feature[];
    }>;
  }>;
}

export const SolutionSlider: React.FC<SolutionSliderProps> = ({
  backgroundColor,
  title,
  headline,
  description,
  container,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [key, setKey] = useState(0);

  const slides = container[0]?.slides || [];

  // Move hooks before any conditional returns
  useEffect(() => {
    let timer: NodeJS.Timeout;

    const startTimer = () => {
      timer = setTimeout(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
        setKey((prevKey) => prevKey + 1);
      }, SLIDE_DURATION);
    };

    startTimer();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentSlide, slides?.length]);

  return (
    <VStack
      spacing={['3rem', '4rem']}
      textAlign="center"
      py={['3rem', '7rem']}
      backgroundColor={backgroundColor || 'transparent'}
    >
      <SectionHeader
        eyebrow={title}
        title={headline}
        description={description?.childMarkdownRemark?.rawMarkdownBody}
        titleMaxWidth="800px"
      />

      <Box
        as={motion.div}
        width="100%"
        height={['auto', '680px']}
        padding={['0.25rem', '1rem 3rem']}
        backgroundColor="blue.50"
        borderRadius="1rem"
        display="flex"
        flexDirection={['column', 'row']}
        justifyContent="space-between"
        alignItems="center"
        maxWidth="1440px"
        margin="0 auto"
        overflow={['visible', 'hidden']}
        backgroundSize={['cover']}
        backgroundPosition={['top center', 'center']}
        backgroundImage={[
          `url(${
            slides?.[currentSlide]?.mobileBackgroundImage?.file?.url ||
            slides?.[currentSlide]?.backgroundImage?.file?.url ||
            ''
          })`,
          `url(${slides?.[currentSlide]?.backgroundImage?.file?.url || ''})`,
        ]}
        key={currentSlide}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2 } as any}
      >
        <Box display={['none', 'block']}>
          <Flex
            direction={['row', 'column']}
            gap={['0.17rem', '1rem']}
            transform={['translateX(0.375rem)', 'translateX(0)']}
          >
            {slides.map((slide, index: number) => (
              <Flex
                key={`${key}-${index}`}
                direction={['column', 'row']}
                align="center"
                gap={['0', '1rem']}
                cursor="pointer"
                data-testid={`desktop-nav-${slide.navigationLabel}`}
                data-active={currentSlide === index}
                onClick={() => {
                  setCurrentSlide(index);
                  setKey((prevKey) => prevKey + 1);
                }}
              >
                <ProgressIndicator isActive={currentSlide === index} />
                <Text
                  color="gray.900"
                  fontSize="1rem"
                  fontWeight={500}
                  opacity={currentSlide === index ? 1 : 0.5}
                  textAlign={['center', 'left']}
                >
                  {slides[index].navigationLabel}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Box>

        <AnimatePresence mode="wait">
          <Box
            as={motion.div}
            width={['94%', '405px']}
            padding="2rem"
            backgroundColor="white"
            borderRadius="1rem"
            display="flex"
            flexDirection="column"
            gap="2rem"
            marginTop={['250px', '0']}
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 } as any}
          >
            <VStack align="flex-start" spacing="0.5rem">
              <Text
                fontSize="1.5rem"
                lineHeight="2.1rem"
                color="gray.900"
                width="100%"
                textAlign="left"
              >
                {slides[currentSlide]?.slideHeading}
              </Text>
              <Text
                fontSize="0.875rem"
                lineHeight="1.3125rem"
                color="gray.600"
                width="100%"
                textAlign="left"
              >
                {slides[currentSlide]?.description?.childMarkdownRemark
                  ?.rawMarkdownBody || ''}
              </Text>
            </VStack>

            <VStack width="100%" spacing="1.25rem" align="flex-start">
              {slides[currentSlide]?.features &&
                slides[currentSlide]?.features?.map(
                  (feature: Feature, index: number) => (
                    <HStack
                      key={`${key}-${index}`}
                      spacing="0.75rem"
                      width="100%"
                    >
                      <Box
                        width="32px"
                        height="32px"
                        backgroundImage={`url(${feature.icon.url})`}
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                        aria-label={feature.text}
                      />
                      <Text
                        flex={1}
                        textAlign="left"
                        color="gray.600"
                        fontSize="0.875rem"
                        lineHeight="1.3125rem"
                      >
                        {feature.text}
                      </Text>
                    </HStack>
                  ),
                )}
            </VStack>

            {slides[currentSlide]?.ctaButton && (
              <Link
                as={GatsbyLink}
                to={slides[currentSlide]?.ctaButton?.url || '#'}
                textAlign="left"
              >
                <Button
                  size="lg"
                  height="3.25rem"
                  px="1.5rem"
                  ml="0"
                  bg="#012b3b"
                  color="white"
                  borderRadius="1.75rem"
                  width="fit-content"
                  _hover={{ bg: '#023d52' }}
                >
                  {slides[currentSlide]?.ctaButton?.label || ''}
                </Button>
              </Link>
            )}
          </Box>
        </AnimatePresence>

        <Box display={['block', 'none']} width="100%">
          <Flex direction="row" gap="0.17rem" justify="center">
            {slides.map((slide, index: number) => (
              <Flex
                key={`${key}-${index}`}
                direction="column"
                align="center"
                gap="0"
                cursor="pointer"
                data-testid={`mobile-nav-${slide.navigationLabel}`}
                onClick={() => {
                  setCurrentSlide(index);
                  setKey((prevKey) => prevKey + 1);
                }}
                width="80px"
              >
                <ProgressIndicator isActive={currentSlide === index} />
                <Text
                  display={['none', 'block']}
                  color="gray.900"
                  fontSize="1rem"
                  fontWeight={500}
                  opacity={currentSlide === index ? 1 : 0.5}
                  textAlign="center"
                  width="100%"
                >
                  {slides[index]?.navigationLabel}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Box>
      </Box>
    </VStack>
  );
};

export default SolutionSlider;
